import React from 'react';

/**
 * parseDevelopment
 * Parses strings like "[+7,+9]", "[-3,+5]", "[+,+]", "[-,-]", "[-,+]"
 * into { min, max, mid } numeric values in the range [-10, 10].
 */
const parseDevelopment = (dev) => {
  if (!dev || typeof dev !== 'string') return { min: 0, max: 0, mid: 0 };

  const trimmed = dev.trim();

  // 1. 尝试匹配范围格式 [min, max]
  const rangeMatch = trimmed.match(/\[\s*([+-]?\d+|[+-])\s*,\s*([+-]?\d+|[+-])\s*\]/);
  
  const pv = (s) => {
    if (!s) return 0;
    s = s.trim();
    if (s === '+') return 6;   // "likely positive"
    if (s === '-') return -6;  // "likely negative"
    const n = parseInt(s, 20);
    return isNaN(n) ? 0 : Math.max(-20, Math.min(20, n));
  };

  if (rangeMatch) {
    const a = pv(rangeMatch[1]);
    const b = pv(rangeMatch[2]);
    return { min: Math.min(a, b), max: Math.max(a, b), mid: (a + b) / 2 };
  }

  // 2. 尝试匹配单一数值格式，例如 "+7", "-3", "5" 或 "[+7]"
  // 移除可能存在的方括号以便解析
  const singleValue = trimmed.replace(/[\[\]]/g, '');
  const n = pv(singleValue);
  
  // 对于单一数值，min, max, mid 均相同，表现为图表上的一点（扇形收缩为线）
  return { min: n, max: n, mid: n };
};

/**
 * MonthlyTimeline — Fan Chart (time-series uncertainty visualization)
 *
 * - X-axis: the 12 future months
 * - Y-axis: development score (-10 to +10)
 * - Fan fill: region between min and max values (widens with time → uncertainty)
 * - Center line: midpoint trend
 * - Dots fade in opacity toward the right (growing uncertainty)
 */
const MonthlyTimeline = ({ timeline, type }) => {
  if (!timeline || timeline.length === 0) return null;

  const isGold = type === 'sixyao';
  const accent      = isGold ? '#c8aa50' : '#4a90d9';
  const accentFaint = isGold ? 'rgba(200,170,80,0.18)' : 'rgba(58,116,184,0.18)';

  // ── SVG geometry ──────────────────────────────────────────────────────────
  const W = 700, H = 220;
  const padL = 38, padR = 16, padT = 22, padB = 60;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const n = timeline.length;

  const parsed = timeline.map(item => parseDevelopment(item.development));

  // Map month index → SVG x/y coordinate
  const xOf = (i) => padL + (n > 1 ? (i / (n - 1)) : 0.5) * chartW;
  const yOf = (v) => padT + chartH / 2 - (v / 20) * (chartH / 2);

  // Gradient IDs (unique per type to avoid conflicts if both render)
  const gradFan  = `fanGrad-${type}`;
  const gradLine = `lineGrad-${type}`;

  // Fan polygon: upper edge L→R, then lower edge R→L
  const upperPts = parsed.map((p, i) => `${xOf(i).toFixed(1)},${yOf(p.max).toFixed(1)}`);
  const lowerPts = parsed.map((p, i) => `${xOf(i).toFixed(1)},${yOf(p.min).toFixed(1)}`);
  const fanPoly  = [...upperPts, ...[...lowerPts].reverse()].join(' ');

  // SVG paths
  const toPath = (pts) => pts.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt}`).join(' ');
  const midPath   = toPath(parsed.map((p, i) => `${xOf(i).toFixed(1)} ${yOf(p.mid).toFixed(1)}`));
  const upperPath = toPath(upperPts.map(pt => pt.replace(',', ' ')));
  const lowerPath = toPath(lowerPts.map(pt => pt.replace(',', ' ')));

  return (
    <div className={`monthly-timeline ${isGold ? 'timeline-gold' : 'timeline-blue'}`}>

      {/* Header */}
      <div className="timeline-header-row">
        <span className="timeline-section-title">未来十二个月</span>
        <span className="timeline-uncertainty-note">变数渐增 →</span>
      </div>

      {/* SVG fan chart */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{ width: '100%', minWidth: 340, display: 'block' }}
          aria-label="12-month development fan chart"
        >
          <defs>
            {/* Fan fill: solid accent on left, fades right */}
            <linearGradient id={gradFan} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor={accent} stopOpacity="0.40" />
              <stop offset="100%" stopColor={accent} stopOpacity="0.06" />
            </linearGradient>
            {/* Center line: solid → faint */}
            <linearGradient id={gradLine} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor={accent} stopOpacity="1.00" />
              <stop offset="100%" stopColor={accent} stopOpacity="0.22" />
            </linearGradient>
          </defs>

          {/* ── Y-axis gridlines & labels ── */}
          {[-20, -10, 0, 10, 20].map(v => {
            const y = yOf(v);
            return (
              <g key={v}>
                <line
                  x1={padL} y1={y} x2={W - padR} y2={y}
                  stroke={v === 0 ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.065)'}
                  strokeWidth={v === 0 ? 1.5 : 0.8}
                  strokeDasharray={v === 0 ? undefined : '3 5'}
                />
                <text
                  x={padL - 4} y={y + 3.5}
                  textAnchor="end"
                  fill="rgba(255,255,255,0.28)"
                  fontSize="9"
                >
                  {v > 0 ? `+${v}` : v}
                </text>
              </g>
            );
          })}

          {/* Zone corner labels */}
          <text x={W - padR} y={padT + 11}           textAnchor="end" fill={accent}                    fontSize="9" fillOpacity="0.55">▲ 利好</text>
          <text x={W - padR} y={padT + chartH - 3}   textAnchor="end" fill="rgba(200,80,80,0.60)"      fontSize="9">▼ 不利</text>

          {/* ── Fan fill ── */}
          <polygon points={fanPoly} fill={`url(#${gradFan})`} />

          {/* Fan boundary edges (dashed) */}
          <path d={upperPath} fill="none" stroke={accent} strokeWidth="0.9" strokeOpacity="0.30" strokeDasharray="3 4" />
          <path d={lowerPath} fill="none" stroke={accent} strokeWidth="0.9" strokeOpacity="0.30" strokeDasharray="3 4" />

          {/* ── Center trend line ── */}
          <path
            d={midPath}
            fill="none"
            stroke={`url(#${gradLine})`}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* ── Dots on midpoints ── */}
          {parsed.map((p, i) => {
            const opacity = 1 - (i / Math.max(n - 1, 1)) * 0.70;
            return (
              <circle
                key={i}
                cx={xOf(i)} cy={yOf(p.mid)}
                r={i < 3 ? 4 : 2.8}
                fill={accent}
                fillOpacity={opacity}
                stroke="rgba(0,0,0,0.45)"
                strokeWidth="0.8"
              />
            );
          })}

          {/* ── Development range labels for first 4 months ── */}
          {parsed.slice(0, 4).map((p, i) => {
            // Place label above the upper fan boundary; clamp to chart bounds
            const rawY = yOf(p.max) - 8;
            const labelY = Math.max(rawY, padT + 8);
            return (
              <text
                key={i}
                x={xOf(i)} y={labelY}
                textAnchor="middle"
                fill={accent}
                fontSize="8.5"
                fillOpacity="0.75"
              >
                {timeline[i].development}
              </text>
            );
          })}

          {/* ── Month labels on X-axis ── */}
          {timeline.map((item, i) => (
            <text
              key={i}
              x={xOf(i)} y={H - padB + 16}
              textAnchor="middle"
              fill={i < 3 ? accent : 'rgba(255,255,255,0.40)'}
              fontSize={i < 3 ? '11' : '10'}
              fontWeight={i < 3 ? '600' : '400'}
            >
              {item.label}
            </text>
          ))}
        </svg>
      </div>

      {/* ── Hint text row (HTML for proper wrapping & CJK support) ── */}
      <div className="timeline-hints-row">
        {timeline.map((item, i) => {
          const opacity = 1 - (i / Math.max(n - 1, 1)) * 0.75;
          return (
            <div
              key={i}
              className="timeline-hint-cell"
              style={{ opacity }}
            >
              {item.hint}
            </div>
          );
        })}
      </div>

      {/* Bottom rule */}
      <div
        className="timeline-bottom-rule"
        style={{ background: `linear-gradient(90deg, ${accentFaint}, transparent)` }}
      />
    </div>
  );
};

export default MonthlyTimeline;
